<template>
  <div class="min-h-screen bg-[#FFF7EF]">
    <div class="mx-auto max-w-4xl px-4 py-12 lg:px-8">
      <div class="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
        <section class="rounded-[32px] border border-gray-100 bg-white p-6 shadow-sm sm:p-8 lg:p-10">
          <p class="text-xs font-semibold uppercase tracking-[0.2em] text-muted">Password assistance</p>
          <h1 class="mt-3 text-3xl font-semibold text-ink">Reset your password</h1>
          <p class="mt-2 text-sm leading-7 text-muted">
            {{ activeRole === 'vendor' ? 'Recover your vendor account access.' : 'Recover your personal account access.' }}
          </p>

          <AuthRoleTabs
            class="mt-6"
            :active-role="activeRole"
            @select="setRole"
          />

          <p v-if="error" class="mt-6 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs font-semibold text-red-700">
            {{ error }}
          </p>
          <p v-if="success" class="mt-6 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-700">
            {{ success }}
          </p>

          <form v-if="step === 'request'" class="mt-6 space-y-4" @submit.prevent="handleRequestReset">
            <label class="block">
              <span class="text-xs font-semibold uppercase tracking-wide text-muted">Email</span>
              <input
                v-model.trim="email"
                type="email"
                required
                placeholder="you@invi8.co"
                class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none"
              >
            </label>

            <button
              type="submit"
              :disabled="loading"
              class="w-full rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brandDark"
            >
              {{ loading ? 'Sending reset code...' : 'Send reset code' }}
            </button>
          </form>

          <form v-else class="mt-6 space-y-4" @submit.prevent="handleVerifyReset">
            <div class="rounded-2xl border border-[#F4E0CF] bg-[#FFF8F1] px-4 py-4 text-sm text-[#7B5D47]">
              Enter the OTP sent to <span class="font-semibold text-ink">{{ email }}</span> and set a new password.
            </div>

            <label class="block">
              <span class="text-xs font-semibold uppercase tracking-wide text-muted">OTP</span>
              <input
                v-model.trim="otp"
                type="text"
                required
                placeholder="Enter OTP"
                autocomplete="one-time-code"
                class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none"
              >
            </label>

            <label class="block">
              <div class="flex items-center justify-between gap-3">
                <span class="text-xs font-semibold uppercase tracking-wide text-muted">New password</span>
                <button type="button" class="text-xs font-semibold text-brand" @click="generateStrongPassword">Generate</button>
              </div>
              <div class="relative mt-2">
                <input
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  autocomplete="new-password"
                  placeholder="Create a new password"
                  class="w-full rounded-xl border border-gray-200 px-4 py-3 pr-12 text-sm text-ink shadow-sm focus:border-brand focus:outline-none"
                >
                <button type="button" class="password-toggle" @click="showPassword = !showPassword" :aria-label="showPassword ? 'Hide password' : 'Show password'">
                  <i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
              </div>
            </label>

            <div class="space-y-2">
              <div class="strength-row">
                <div class="strength-track">
                  <div class="strength-fill" :class="strengthClass" :style="{ width: `${passwordStrength.score}%` }"></div>
                </div>
                <span class="strength-label" :class="strengthClass">{{ passwordStrength.label }}</span>
              </div>
              <p class="text-xs text-muted">Use 8+ characters with uppercase, lowercase, number, and symbol.</p>
            </div>

            <label class="block">
              <span class="text-xs font-semibold uppercase tracking-wide text-muted">Confirm password</span>
              <div class="relative mt-2">
                <input
                  v-model="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  required
                  autocomplete="new-password"
                  placeholder="Repeat your new password"
                  class="w-full rounded-xl border border-gray-200 px-4 py-3 pr-12 text-sm text-ink shadow-sm focus:border-brand focus:outline-none"
                >
                <button type="button" class="password-toggle" @click="showConfirmPassword = !showConfirmPassword" :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'">
                  <i :class="showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
              </div>
            </label>

            <div class="flex flex-wrap items-center gap-3">
              <button
                type="button"
                class="rounded-full border border-gray-200 px-4 py-3 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
                @click="step = 'request'"
              >
                Change email
              </button>
              <button
                type="submit"
                :disabled="loading"
                class="flex-1 rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brandDark"
              >
                {{ loading ? 'Updating password...' : 'Update password' }}
              </button>
            </div>
          </form>

          <div class="mt-8 text-center text-xs text-muted">
            Remembered it?
            <RouterLink class="font-semibold text-brand" :to="loginLink">Back to login</RouterLink>
          </div>
        </section>

        <section class="rounded-[32px] border border-orange-100 bg-white/90 p-8 shadow-sm">
          <p class="text-xs font-semibold uppercase tracking-[0.2em] text-muted">What happens next</p>
          <div class="mt-5 space-y-4 text-sm text-muted">
            <div class="tip-card">
              <span class="tip-badge">1</span>
              <div>
                <p class="font-semibold text-ink">Request a reset code</p>
                <p>Enter the email tied to your Invi8 account.</p>
              </div>
            </div>
            <div class="tip-card">
              <span class="tip-badge">2</span>
              <div>
                <p class="font-semibold text-ink">Check your inbox</p>
                <p>Use the OTP from the reset email to verify the request.</p>
              </div>
            </div>
            <div class="tip-card">
              <span class="tip-badge">3</span>
              <div>
                <p class="font-semibold text-ink">Set a stronger password</p>
                <p>Pick a password you have not used before and keep it private.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AuthRoleTabs from '@/components/user/AuthRoleTabs.vue'
import userApi from '@/services/user/api'
import vendorApi from '@/services/vendor/api'

const router = useRouter()
const route = useRoute()

const step = ref('request')
const email = ref('')
const otp = ref('')
const password = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const error = ref('')
const success = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const activeRole = ref(route.query.role === 'vendor' ? 'vendor' : 'user')

const resetForm = () => {
  step.value = 'request'
  email.value = ''
  otp.value = ''
  password.value = ''
  confirmPassword.value = ''
  error.value = ''
  success.value = ''
  showPassword.value = false
  showConfirmPassword.value = false
}

watch(
  () => route.query.role,
  (role) => {
    const nextRole = role === 'vendor' ? 'vendor' : 'user'
    if (nextRole !== activeRole.value) {
      resetForm()
    }
    activeRole.value = nextRole
  }
)

const loginLink = computed(() => `/login?role=${activeRole.value}`)

const setRole = (role) => {
  if (role === activeRole.value) return
  resetForm()
  router.replace({
    name: 'forgot-password',
    query: {
      ...route.query,
      role
    }
  })
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i

const getPasswordStrength = () => {
  const value = password.value || ''
  let points = 0
  if (value.length >= 8) points += 25
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) points += 25
  if (/\d/.test(value)) points += 25
  if (/[^A-Za-z0-9]/.test(value)) points += 25

  let label = 'Too weak'
  if (points >= 100) label = 'Strong'
  else if (points >= 75) label = 'Good'
  else if (points >= 50) label = 'Fair'
  return { score: points, label }
}

const passwordStrength = computed(() => getPasswordStrength())

const strengthClass = computed(() => {
  if (passwordStrength.value.score >= 100) return 'strength-strong'
  if (passwordStrength.value.score >= 75) return 'strength-good'
  if (passwordStrength.value.score >= 50) return 'strength-fair'
  return 'strength-weak'
})

const generateStrongPassword = () => {
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lowercase = 'abcdefghijkmnopqrstuvwxyz'
  const numbers = '23456789'
  const symbols = '!@#$%^&*_-+='
  const all = uppercase + lowercase + numbers + symbols
  const chars = [
    uppercase[Math.floor(Math.random() * uppercase.length)],
    lowercase[Math.floor(Math.random() * lowercase.length)],
    numbers[Math.floor(Math.random() * numbers.length)],
    symbols[Math.floor(Math.random() * symbols.length)]
  ]
  while (chars.length < 12) {
    chars.push(all[Math.floor(Math.random() * all.length)])
  }
  password.value = chars.sort(() => Math.random() - 0.5).join('')
  confirmPassword.value = password.value
}

const handleRequestReset = async () => {
  error.value = ''
  success.value = ''

  if (!email.value) {
    error.value = 'Email is required.'
    return
  }

  if (!emailRegex.test(email.value)) {
    error.value = 'Please enter a valid email address.'
    return
  }

  loading.value = true
  try {
    const formData = new FormData()
    formData.append('email', email.value.trim().toLowerCase())
    formData.append('role', activeRole.value)
    const { data } =
      activeRole.value === 'vendor'
        ? await vendorApi.requestPasswordReset(formData)
        : await userApi.requestPasswordReset(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Unable to send reset code')
    }
    success.value = data?.message || data?.msg || 'Reset code sent. Check your inbox for the OTP.'
    step.value = 'verify'
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Unable to send reset code'
  } finally {
    loading.value = false
  }
}

const handleVerifyReset = async () => {
  error.value = ''
  success.value = ''

  if (!otp.value) {
    error.value = 'OTP is required.'
    return
  }
  if (passwordStrength.value.score < 100) {
    error.value = 'Password must include uppercase, lowercase, number, special character and be at least 8 characters.'
    return
  }
  if (!confirmPassword.value) {
    error.value = 'Please confirm your new password.'
    return
  }
  if (password.value !== confirmPassword.value) {
    error.value = 'Passwords do not match.'
    return
  }

  loading.value = true
  try {
    const formData = new FormData()
    formData.append('email', email.value.trim().toLowerCase())
    formData.append('otp', otp.value.trim())
    formData.append('password', password.value)
    formData.append('confirm_password', confirmPassword.value)
    formData.append('role', activeRole.value)
    const { data } =
      activeRole.value === 'vendor'
        ? await vendorApi.verifyPasswordReset(formData)
        : await userApi.verifyPasswordReset(formData)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.msg || data?.message || 'Unable to update password')
    }
    router.push(loginLink.value)
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Unable to update password'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.password-toggle {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #6b7280;
}

.strength-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.strength-track {
  flex: 1;
  height: 10px;
  border-radius: 999px;
  background: #f3f4f6;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.2s ease, background-color 0.2s ease;
}

.strength-weak {
  background: #ef4444;
  color: #b91c1c;
}

.strength-fair {
  background: #f59e0b;
  color: #b45309;
}

.strength-good {
  background: #10b981;
  color: #047857;
}

.strength-strong {
  background: #059669;
  color: #065f46;
}

.strength-label {
  min-width: 72px;
  font-size: 12px;
  font-weight: 700;
  text-align: right;
}

.tip-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  border-radius: 22px;
  border: 1px solid #f4e0cf;
  background: #fff9f3;
  padding: 18px;
}

.tip-badge {
  display: inline-flex;
  height: 32px;
  width: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #fff;
  color: #ec2263;
  font-size: 13px;
  font-weight: 800;
  box-shadow: 0 8px 20px rgba(236, 34, 99, 0.08);
}
</style>
